import React, { useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import styles from './bodywon.module.css';
import { createCustomerAccount } from '../utils/registerApi';

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  password: '',
  confirmPassword: '',
  mobile: '',
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const CreateAccountModal = ({ show, onHide, onCreated }) => {
  const [form, setForm] = useState(initialForm);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm(initialForm);
    setError('');
    setShowPassword(false);
  };

  const handleHide = () => {
    resetForm();
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.firstName.trim()) {
      setError('First name is required.');
      return;
    }
    if (!form.lastName.trim()) {
      setError('Last name is required.');
      return;
    }
    if (!form.email.trim()) {
      setError('Email is required.');
      return;
    }
    if (!isValidEmail(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!form.username.trim()) {
      setError('Username is required.');
      return;
    }
    if (!form.password.trim()) {
      setError('Password is required.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSaving(true);
    try {
      await createCustomerAccount(form);
      toast.success('Account created successfully. Please login.');
      onCreated?.(form.username.trim());
      handleHide();
    } catch (err) {
      setError(err.message || 'Could not create account. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleHide} centered className={styles.createAccountModal}>
      <Modal.Header closeButton>
        <Modal.Title className={styles.createAccountTitle}>Create Account</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              name="firstName"
              placeholder="First Name *"
              className={styles.loginInput}
              value={form.firstName}
              onChange={handleChange}
              disabled={saving}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              name="lastName"
              placeholder="Last Name *"
              className={styles.loginInput}
              value={form.lastName}
              onChange={handleChange}
              disabled={saving}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Control
              type="email"
              name="email"
              placeholder="Email *"
              className={styles.loginInput}
              value={form.email}
              onChange={handleChange}
              disabled={saving}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              name="username"
              placeholder="Username *"
              className={styles.loginInput}
              value={form.username}
              onChange={handleChange}
              disabled={saving}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <div className={styles.passwordWrapper}>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password *"
                className={styles.loginInput}
                value={form.password}
                onChange={handleChange}
                disabled={saving}
                required
              />
              <span
                onClick={() => setShowPassword((value) => !value)}
                className={styles.passwordToggle}
              >
                {showPassword ? <FiEye /> : <FiEyeOff />}
              </span>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <div className={styles.passwordWrapper}>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                placeholder="Confirm Password *"
                className={styles.loginInput}
                value={form.confirmPassword}
                onChange={handleChange}
                disabled={saving}
                required
              />
              <span
                onClick={() => setShowPassword((value) => !value)}
                className={styles.passwordToggle}
              >
                {showPassword ? <FiEye /> : <FiEyeOff />}
              </span>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Control
              type="tel"
              name="mobile"
              placeholder="Mobile Number (optional)"
              className={styles.loginInput}
              value={form.mobile}
              onChange={handleChange}
              disabled={saving}
            />
          </Form.Group>

          {error && (
            <div className={styles.createAccountError}>{error}</div>
          )}

          <Button
            variant="primary"
            type="submit"
            className={styles.loginButton}
            disabled={saving}
          >
            {saving ? 'Creating...' : 'Create Account'}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateAccountModal;
